import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatApiError } from "@/lib/format";

export const AuthModal = ({ open, mode = "login", onOpenChange }) => {
  const [tab, setTab] = useState(mode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("buyer");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register, setSession } = useAuth();

  // Mobile OTP flow state (tab === "mobile")
  const [mobLocal, setMobLocal] = useState("");
  const [mobStep, setMobStep] = useState("send");
  const [mobCode, setMobCode] = useState("");
  const [mobMock, setMobMock] = useState(false);
  const [mobResendIn, setMobResendIn] = useState(0);
  const [mobChannel, setMobChannel] = useState("sms"); // "sms" | "whatsapp"
  const mobFull = `+91${mobLocal}`;
  const mobValid = /^[6-9]\d{9}$/.test(mobLocal);

  useEffect(() => {
    setTab(mode);
  }, [mode, open]);

  // Mobile resend countdown
  useEffect(() => {
    if (mobResendIn <= 0) return;
    const t = setTimeout(() => setMobResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [mobResendIn]);

  const mobileSend = async (e, channelOverride) => {
    if (e && e.preventDefault) e.preventDefault();
    const channel = channelOverride || mobChannel;
    setMobChannel(channel);
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mobile/send-otp", { phone: mobFull, channel });
      setMobMock(data.mock);
      setMobStep("verify");
      setMobResendIn(data.cooldown_sec || 60);
      const via = (data.channel || channel) === "whatsapp" ? "WhatsApp" : "SMS";
      toast.success(data.mock ? "Mock mode: use 123456 as the code" : `OTP sent to ${mobFull} via ${via}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const reason = (detail && typeof detail === "object" && detail.reason) || null;
      const retry = err.response?.headers?.["retry-after"]
        ? parseInt(err.response.headers["retry-after"], 10)
        : (detail && detail.retry_after) || 0;
      if (reason === "cooldown" && retry > 0 && retry <= 90) {
        setMobResendIn(retry);
        setMobStep("verify");
        toast.info(`A code was already sent to ${mobFull}. Enter it below, or resend in ${retry}s.`);
      } else if (retry > 0) {
        setMobResendIn(retry);
        const mins = Math.ceil(retry / 60);
        toast.error(`Too many OTP requests for this number. Please try again in ~${mins} min.`);
      } else {
        toast.error(formatApiError(detail) || "Failed to send OTP");
      }
    } finally {
      setBusy(false);
    }
  };

  const mobileVerify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mobile/verify-otp", { phone: mobFull, code: mobCode });
      setSession(data.token, data.user);
      toast.success(`Welcome, ${data.user.name}!`);
      onOpenChange(false);
      setMobStep("send"); setMobCode(""); setMobLocal("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = tab === "login"
      ? await login(email, password)
      : await register({ name, email, password, role, phone });
    setBusy(false);
    if (res.ok) {
      toast.success(`Welcome, ${res.user.name}!`);
      onOpenChange(false);
    } else {
      toast.error(res.error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">
            {tab === "login" ? "Welcome back" : "Join NimmaSwathu"}
          </DialogTitle>
          <DialogDescription>
            {tab === "login" ? "Sign in to your estate dashboard." : "List, save and inquire — all in one place."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 mb-4" data-testid="auth-tabs">
          <button
            type="button"
            onClick={() => setTab("login")}
            data-testid="auth-tab-login"
            className={`flex-1 py-2 rounded-full text-xs font-medium transition-colors ${tab === "login" ? "bg-[var(--teak)] text-white" : "bg-[var(--surface)] text-[var(--ink-muted)]"}`}
          >
            Sign In
          </button>
          <button
            type="button"
            onClick={() => setTab("register")}
            data-testid="auth-tab-register"
            className={`flex-1 py-2 rounded-full text-xs font-medium transition-colors ${tab === "register" ? "bg-[var(--teak)] text-white" : "bg-[var(--surface)] text-[var(--ink-muted)]"}`}
          >
            Create Account
          </button>
          <button
            type="button"
            onClick={() => setTab("mobile")}
            data-testid="auth-tab-mobile"
            className={`flex-1 py-2 rounded-full text-xs font-medium transition-colors ${tab === "mobile" ? "bg-[var(--teak)] text-white" : "bg-[var(--surface)] text-[var(--ink-muted)]"}`}
          >
            Mobile OTP
          </button>
        </div>

        {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
        <button
          type="button"
          onClick={() => {
            const redirectUrl = window.location.origin + "/";
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }}
          className="w-full flex items-center justify-center gap-3 py-2.5 rounded-full border border-[var(--line-strong)] bg-white hover:bg-[var(--surface)] text-sm font-medium transition-colors"
          data-testid="google-signin-btn"
        >
          <svg className="w-4 h-4" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.9 32.4 29.4 35.5 24 35.5c-6.4 0-11.6-5.2-11.6-11.5S17.6 12.5 24 12.5c3 0 5.7 1.1 7.7 3l5.7-5.7C33.9 6.6 29.2 4.8 24 4.8 13.4 4.8 4.8 13.4 4.8 24S13.4 43.2 24 43.2c11.1 0 19-7.8 19-19 0-1.3-.1-2.5-.4-3.7z" />
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.9 19 12.5 24 12.5c3 0 5.7 1.1 7.7 3l5.7-5.7C33.9 6.6 29.2 4.8 24 4.8 16.3 4.8 9.7 9.1 6.3 14.7z" />
            <path fill="#4CAF50" d="M24 43.2c5.2 0 9.8-1.8 13.4-4.8l-6.2-5c-1.9 1.3-4.4 2.1-7.2 2.1-5.4 0-9.9-3.6-11.5-8.4l-6.5 5C9.6 38.7 16.2 43.2 24 43.2z" />
            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.9 2.3-2.4 4.2-4.4 5.5l6.2 5c-.4.4 6.9-5 6.9-14.5 0-1.3-.1-2.5-.4-3.5z" />
          </svg>
          Continue with Google
        </button>

        <div className="my-4 divider-ornament text-[10px] uppercase tracking-wider text-[var(--ink-muted)]">
          <span>or</span>
        </div>

        {tab === "mobile" ? (
          mobStep === "send" ? (
            <form onSubmit={mobileSend} className="space-y-4" data-testid="mobile-otp-send-form">
              <div>
                <Label>Indian mobile number</Label>
                <div className="flex gap-2 mt-1">
                  <div className="w-24 px-3 py-2 rounded-md border border-[var(--line)] bg-[var(--surface)] text-sm font-mono text-center">+91</div>
                  <Input
                    required
                    type="tel"
                    inputMode="numeric"
                    placeholder="98xxxxxxxx"
                    value={mobLocal}
                    onChange={(e) => setMobLocal(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    className="flex-1 font-mono tracking-wide"
                    data-testid="mobile-otp-phone-input"
                    autoComplete="tel-national"
                  />
                </div>
                <p className="text-xs text-[var(--ink-muted)] mt-1">We'll send a 6-digit SMS code. No password needed.</p>
              </div>
              <button
                type="submit"
                disabled={busy || !mobValid}
                className="btn-primary w-full"
                data-testid="mobile-otp-send-btn"
              >
                {busy && mobChannel === "sms" ? "Sending…" : "Send OTP via SMS"}
              </button>
              <button
                type="button"
                disabled={busy || !mobValid}
                onClick={() => mobileSend(null, "whatsapp")}
                className="btn-ghost w-full flex items-center justify-center gap-2"
                data-testid="mobile-otp-send-wa-btn"
                aria-label="Send OTP via WhatsApp"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M20.52 3.48A11.85 11.85 0 0 0 12.06 0C5.5 0 .17 5.33.17 11.9c0 2.1.55 4.14 1.6 5.95L0 24l6.3-1.66a11.9 11.9 0 0 0 5.75 1.47h.01c6.56 0 11.89-5.33 11.89-11.9 0-3.17-1.24-6.16-3.43-8.43ZM12.07 21.8h-.01a9.86 9.86 0 0 1-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.86 9.86 0 0 1-1.52-5.27C2.17 6.44 6.59 2.02 12.07 2.02c2.63 0 5.1 1.03 6.96 2.9a9.78 9.78 0 0 1 2.88 6.98c0 5.47-4.42 9.9-9.84 9.9Zm5.42-7.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.95 1.17-.17.2-.35.22-.65.07a8.12 8.12 0 0 1-2.4-1.48 8.95 8.95 0 0 1-1.65-2.06c-.17-.3-.02-.46.13-.6.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.6-.92-2.2-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.8.37-.27.3-1.05 1.03-1.05 2.5s1.07 2.9 1.22 3.1c.15.2 2.1 3.2 5.08 4.49.71.3 1.26.48 1.69.62.71.22 1.35.19 1.86.12.57-.08 1.76-.72 2-1.42.25-.7.25-1.3.17-1.42-.08-.12-.27-.2-.57-.35Z"/>
                </svg>
                {busy && mobChannel === "whatsapp" ? "Sending…" : "Send via WhatsApp instead"}
              </button>
            </form>
          ) : (
            <form onSubmit={mobileVerify} className="space-y-4" data-testid="mobile-otp-verify-form">
              <div>
                <Label>Enter 6-digit code sent to {mobFull}</Label>
                <Input
                  required
                  maxLength={6}
                  placeholder="Enter 6-digit code"
                  value={mobCode}
                  onChange={(e) => setMobCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="font-mono text-lg tracking-widest text-center"
                  data-testid="mobile-otp-code-input"
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  autoFocus
                />
                {mobMock && (
                  <p className="text-xs text-[var(--primary)] mt-1" data-testid="mobile-otp-mock-notice">
                    Mock mode active — use <strong>123456</strong>.
                  </p>
                )}
              </div>
              <button
                type="submit"
                disabled={busy || mobCode.length !== 6}
                className="btn-primary w-full"
                data-testid="mobile-otp-verify-btn"
              >
                {busy ? "Verifying…" : "Verify & Sign In"}
              </button>
              <button
                type="button"
                onClick={() => mobileSend(null, mobChannel)}
                disabled={busy || mobResendIn > 0}
                className="btn-ghost w-full"
                data-testid="mobile-otp-resend-btn"
              >
                {mobResendIn > 0
                  ? `Resend ${mobChannel === "whatsapp" ? "WhatsApp" : "SMS"} in ${mobResendIn}s`
                  : `Resend ${mobChannel === "whatsapp" ? "WhatsApp" : "SMS"} code`}
              </button>
              <button
                type="button"
                onClick={() => mobileSend(null, mobChannel === "whatsapp" ? "sms" : "whatsapp")}
                disabled={busy || mobResendIn > 0}
                className="w-full text-xs text-[var(--primary)] hover:underline disabled:opacity-50"
                data-testid="mobile-otp-switch-channel-btn"
              >
                Didn't get it? Try {mobChannel === "whatsapp" ? "SMS" : "WhatsApp"} instead
              </button>
              <button
                type="button"
                onClick={() => { setMobStep("send"); setMobCode(""); }}
                className="w-full text-xs text-[var(--ink-muted)] hover:text-[var(--primary)]"
              >
                Change number
              </button>
            </form>
          )
        ) : (
        <form onSubmit={submit} className="space-y-4">
          {tab === "register" && (
            <>
              <div>
                <Label htmlFor="auth-name">Full Name</Label>
                <Input id="auth-name" data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
              <div>
                <Label htmlFor="auth-phone">Phone (optional)</Label>
                <Input id="auth-phone" data-testid="auth-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98xxxxxxxx" />
              </div>
              <div>
                <Label>I am a...</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger data-testid="auth-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="buyer" data-testid="role-option-buyer">Looking to buy or rent</SelectItem>
                    <SelectItem value="owner" data-testid="role-option-owner">Property Owner</SelectItem>
                    <SelectItem value="agent" data-testid="role-option-agent">Real Estate Agent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
          <div>
            <Label htmlFor="auth-email">Email</Label>
            <Input id="auth-email" type="email" data-testid="auth-email-input" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div>
            <Label htmlFor="auth-password">Password</Label>
            <Input id="auth-password" type="password" minLength={6} data-testid="auth-password-input" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="auth-submit-btn">
            {busy ? "Please wait..." : tab === "login" ? "Sign In" : "Create Account"}
          </button>
        </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
